import React, { useState } from 'react';
import { CircularProgress } from '@mui/material';
import { Box, Grid, Typography, Divider, useTheme, TextField, Button, IconButton } from '@mui/material';
import { FaInstagram, FaWhatsapp, FaLinkedinIn, FaTwitter, FaFacebook } from 'react-icons/fa';

import COLORS from '../../constants/colors';
import { toast } from 'react-toastify';
import baseURL from '../pages/admin/config';

const Footer = () => {

  const [loading, setLoading] = useState(false);

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  return (
    <Box
      component="footer"
      sx={{
        position: 'relative',
        // --- MODIFIED: Changed main background to light grey ---
        backgroundColor: COLORS.lightGray, // Using COLORS.lightGray for background
        color: COLORS.neutral800, // Default text color for the footer (dark text on light background)
        py: { xs: 8, md: 10 },
        px: { xs: 3, sm: 6, md: 10 },
        mt: 8,
        overflow: 'hidden',
        // Removed the gradient overlay as it's typically used for dark backgrounds
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: 'none', // No gradient needed on light background
          zIndex: 0,
        },
      }}
    >
      {/* Content container */}
      <Grid container spacing={6} sx={{ position: 'relative', zIndex: 1 }}>
        {/* Company Info */}
        <Grid item xs={12} md={3}>
          <Typography variant="h5" fontWeight="700" gutterBottom>
            Yamba
          </Typography>
          <Divider
            sx={{
              width: 60,
              height: 4,
              backgroundColor: COLORS.primary, // Divider color to stand out
              mb: 2,
              borderRadius: 2,
            }}
          />
          <Typography
            variant="body2"
            sx={{
              color: COLORS.neutral700, // Darker neutral for main text
              lineHeight: 1.7,
            }}
          >
            Achieve your fundraising dreams with Yamba. Fast, secure, and seamless transactions designed for real impact. Join us today!
          </Typography>
        </Grid>

        {/* Quick Links */}
        <Grid item xs={12} md={2}>
          <Typography variant="h6" fontWeight="600" gutterBottom>
            Quick Links
          </Typography>
          {/* Explicit Home link */}
          <Typography
            component="a"
            href="/"
            sx={{
              display: 'block',
              color: COLORS.neutral700,
              textDecoration: 'none',
              mb: 1.2,
              fontSize: '0.95rem',
              transition: 'color 0.3s ease',
              '&:hover': {
                color: COLORS.primary, // Primary color on hover
              },
            }}
          >
            Home
          </Typography>

          {/* Other links */}
          {['Campaigns', 'How it works', 'Impact', 'Contact-us'].map((link) => (
            <Typography
              key={link}
              component="a"
              href={`/${link.toLowerCase().replace(/\s/g, '-')}`}
              sx={{
                display: 'block',
                color: COLORS.neutral700,
                textDecoration: 'none',
                mb: 1.2,
                fontSize: '0.95rem',
                transition: 'color 0.3s ease',
                '&:hover': {
                  color: COLORS.primary, // Primary color on hover
                },
              }}
            >
              {link}
            </Typography>
          ))}
        </Grid>

        {/* Contact */}
        <Grid item xs={12} md={3}>
          <Typography variant="h6" fontWeight="600" gutterBottom>
            Contact Us
          </Typography>
          <Divider sx={{ width: 40, height: 3, backgroundColor: COLORS.primary, mb: 2, borderRadius: 2 }} />
          <Typography variant="body2" sx={{ mb: 1.5, color: COLORS.neutral700 }}>
            <strong>Phone:</strong>{' '}
            <a href="tel:+256752627003" style={{ color: 'inherit', textDecoration: 'none' }}>
              0752 627 003
            </a>
            {' / '}
            <a href="tel:+256790359312" style={{ color: 'inherit', textDecoration: 'none' }}>
              0790 359 312
            </a>
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5, color: COLORS.neutral700 }}>
            <strong>Email:</strong>{' '}
            <a href="mailto:support@yambaafrica.com" style={{ color: 'inherit', textDecoration: 'none' }}>
              support@yambaafrica.com
            </a>
          </Typography>
          <Typography variant="body2" sx={{ color: COLORS.neutral700 }}>
            <strong>Address:</strong> Ntinda, Kampala, Uganda
          </Typography>
        </Grid>

        {/* Socials & Newsletter */}
        <Grid item xs={12} md={4}>
          <Typography variant="h6" fontWeight="600" gutterBottom>
            Follow Us
          </Typography>
          <Divider sx={{ width: 40, height: 3, backgroundColor: COLORS.primary, mb: 2, borderRadius: 2 }} />
          <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
            {[
              { icon: <FaInstagram size={20} />, href: 'https://www.instagram.com/yamba_africa/' },
              { icon: <FaLinkedinIn size={20} />, href: 'https://www.linkedin.com/company/yamba-africa' },
              { icon: <FaTwitter size={20} />, href: 'https://x.com/yamba_africa' },
              { icon: <FaWhatsapp size={20} />, href: 'https://wa.me/256752627003' },
              { icon: <FaFacebook size={20} />, href: 'https://www.facebook.com/yambaafrica' },
            ].map(({ icon, href }, i) => (
              <IconButton
                key={i}
                component="a"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: COLORS.primary, // Primary color for icons
                  bgcolor: 'rgba(0,0,0,0.05)', // Very light transparent dark background
                  border: `1px solid ${COLORS.neutral300}`, // Light border
                  transition: 'background-color 0.3s ease, transform 0.3s ease, color 0.3s ease',
                  borderRadius: '50%',
                  width: 40,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': {
                    bgcolor: COLORS.primary, // Primary background on hover
                    color: COLORS.white, // White icon on hover
                    transform: 'scale(1.05)',
                    borderColor: COLORS.primary, // Primary border on hover
                  },
                }}
              >
                {icon}
              </IconButton>
            ))}
          </Box>

          {/* Newsletter */}
          <Typography variant="h6" fontWeight="600" gutterBottom>
            Stay Updated
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: COLORS.neutral700 }}>
            Join our newsletter for impactful stories and updates.
          </Typography>
          <Box
            component="form"
            onSubmit={async (e) => {
  e.preventDefault();
  const formData = new FormData(e.target);
  const email = formData.get('email');


   setLoading(true); // Start loading

  try {
    const response = await fetch(`${baseURL}/data/backend/newsletter/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    if (response.ok) {
      toast.success('Thank you for subscribing to our newsletter!');
      e.target.reset();
    } else if (response.status === 409) {
      toast.info('You are already subscribed!');
    } else {
      const data = await response.json();
      toast.error(data?.error || 'Something went wrong. Try again.');
    }
  } catch (error) {
    toast.error('Network error. Please try again later.');
  }finally {
    setLoading(false); // Stop loading
  }
}}
            sx={{ display: 'flex', gap: 1.5, maxWidth: 400, flexWrap: 'wrap', mt: 1 }}
          >
            <TextField
  placeholder="Your email address"
  name="email"
  variant="outlined"
  size="small"
  sx={{
    bgcolor: COLORS.white,
    borderRadius: 4,
    flexGrow: 1,
    minWidth: '220px',
    '& .MuiOutlinedInput-root': {
      color: COLORS.neutral800,
      borderRadius: '12px',
      height: '50px',
      '& input': {
        padding: '10px 14px',
        fontSize: '14px', // Adjust font size of input text
      },
      '& fieldset': {
        borderColor: COLORS.neutral400,
      },
      '&:hover fieldset': {
        borderColor: COLORS.primary,
      },
      '&.Mui-focused fieldset': {
        borderColor: COLORS.primary,
      },
    },
    '& .MuiInputBase-input::placeholder': {
      color: COLORS.neutral500,
      fontSize: '14px', // 👈 Smaller placeholder font size
      opacity: 1,
    },
  }}
  required
  type="email"
/>

            <Button
  type="submit"
  variant="contained"
  disabled={loading}
  sx={{
    bgcolor: COLORS.primary,
    color: COLORS.white,
    fontWeight: 'bold',
    px: 3,
    whiteSpace: 'nowrap',
    textTransform: 'none',
    mt: { xs: 1.5, sm: 0 },
    minWidth: '120px',
    borderRadius: 3,
    boxShadow: 0,
    '&:disabled': {
      bgcolor: COLORS.primary, // Keep button color while disabled
      opacity: 0.8,
    },
  }}
>
  {loading ? <CircularProgress size={20} sx={{ color: COLORS.white }} /> : 'Subscribe'}
</Button>

          </Box>
        </Grid>
      </Grid>

      {/* Copyright */}
      <Typography
        variant="body2"
        sx={{
          textAlign: 'center',
          mt: 6,
          color: COLORS.neutral600, // Neutral color for copyright text
          fontSize: '0.875rem',
          position: 'relative',
          zIndex: 1,
          userSelect: 'none',
          flexWrap: 'wrap',
          display: 'flex',
          justifyContent: 'center',
          gap: { xs: '8px', md: '12px' },
        }}
      >
        <span>&copy; Yamba {new Date().getFullYear()} All rights reserved</span>
        <a
          href="/privacy-policy"
          style={{ color: COLORS.primary, textDecoration: 'none', fontWeight: '600' }} // Primary color for links
        >
          Privacy Policy
        </a>
        <a
          href="/terms-of-use"
          style={{ color: COLORS.primary, textDecoration: 'none', fontWeight: '600' }} // Primary color for links
        >
          Terms & Conditions
        </a>
      </Typography>
    </Box>
  );
};

export default Footer;